import React from 'react';
import { Search, ShoppingCart, Truck, Plus } from 'lucide-react';
import { formatMoney, onAccent, type StorefrontConfig } from '../types';

/**
 * Market — food & grocery.
 *
 * Convention research (Dribbble, grocery/supermarket e-commerce): this
 * vertical is the opposite of Boutique. Shoppers arrive knowing what they
 * want and buy the same things repeatedly, so the layouts lead with search
 * and categories, put the delivery promise above the fold, and pack the grid
 * with small tiles that each carry a price and an add button. Discounts are
 * shouted, not whispered.
 *
 * What that means here: a utility bar with search and cart, a delivery strip,
 * a scrollable category rail, and a dense grid where every tile can be added
 * to a basket without opening the product.
 */
export default function Market({ config }: { config: StorefrontConfig }) {
  const ink = '#16191D';
  const accentText = onAccent(config.accent);

  return (
    <div style={{ background: '#F7F7F7', color: ink, fontFamily: 'var(--font-family)', minHeight: '100%' }}>
      <header style={{ background: '#FFFFFF', borderBottom: '1px solid #E0E4E8' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(10px, 2vw, 20px)',
            padding: '14px clamp(14px, 4vw, 40px)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.01em', marginRight: 4 }}>
            {config.name}
          </span>

          <label
            style={{
              flex: '1 1 260px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 42,
              padding: '0 14px',
              borderRadius: 999,
              background: '#F7F7F7',
              border: '1px solid #E0E4E8',
            }}
          >
            <Search size={16} color="#8E9CAA" />
            <span style={{ fontSize: 13, color: '#8E9CAA' }}>Search {config.name}</span>
          </label>

          <button
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 42,
              padding: '0 18px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: config.accent,
              color: accentText,
              fontWeight: 600,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          >
            <ShoppingCart size={16} />
            Basket
          </button>
        </div>

        {/* The delivery promise is the single most load-bearing line on a
            grocery store — it decides whether the basket is worth starting. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px clamp(14px, 4vw, 40px)',
            borderTop: '1px solid #F0F1F3',
            fontSize: 13,
          }}
        >
          <Truck size={15} color={config.accent} />
          <strong style={{ fontWeight: 600 }}>{config.announcement ?? 'Same-day delivery available'}</strong>
          <span style={{ opacity: 0.55, marginLeft: 'auto' }}>{config.location}</span>
        </div>
      </header>

      <section style={{ padding: 'clamp(16px, 3vw, 28px) clamp(14px, 4vw, 40px) 0' }}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            overflowX: 'auto',
            paddingBottom: 6,
            scrollbarWidth: 'thin',
          }}
        >
          {config.collections.map((c, i) => (
            <button
              key={c}
              type="button"
              style={{
                flex: '0 0 auto',
                height: 38,
                padding: '0 16px',
                borderRadius: 999,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                border: i === 0 ? 'none' : '1px solid #E0E4E8',
                background: i === 0 ? ink : '#FFFFFF',
                color: i === 0 ? '#FFFFFF' : ink,
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section style={{ padding: 'clamp(16px, 3vw, 28px) clamp(14px, 4vw, 40px) clamp(40px, 6vw, 72px)' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Popular this week</h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 172px), 1fr))',
            gap: 'clamp(10px, 1.5vw, 16px)',
          }}
        >
          {config.products.map((p) => (
            <article
              key={p.id}
              style={{
                background: '#FFFFFF',
                border: '1px solid #E0E4E8',
                borderRadius: 14,
                padding: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                opacity: p.soldOut ? 0.55 : 1,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  borderRadius: 10,
                  background: `linear-gradient(150deg, ${p.swatch[0]}, ${p.swatch[1]})`,
                }}
              >
                {p.badge ? (
                  <span
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      background: '#D70E0E',
                      color: '#FFFFFF',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '4px 7px',
                      borderRadius: 6,
                    }}
                  >
                    {p.badge}
                  </span>
                ) : null}
              </div>

              <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{p.name}</span>

              <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'grid', flex: 1 }}>
                  {config.showPrices ? (
                    <>
                      <strong style={{ fontSize: 15 }}>{formatMoney(p.price, config.currency)}</strong>
                      {p.compareAt ? (
                        <span style={{ fontSize: 11, opacity: 0.45, textDecoration: 'line-through' }}>
                          {formatMoney(p.compareAt, config.currency)}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span style={{ fontSize: 12, opacity: 0.6 }}>Ask for price</span>
                  )}
                </div>

                <button
                  type="button"
                  disabled={p.soldOut}
                  aria-label={p.soldOut ? `${p.name} out of stock` : `Add ${p.name}`}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    border: 'none',
                    cursor: p.soldOut ? 'not-allowed' : 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                    background: p.soldOut ? '#E0E4E8' : config.accent,
                    color: p.soldOut ? '#8E9CAA' : accentText,
                  }}
                >
                  <Plus size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <footer
        style={{
          background: '#FFFFFF',
          borderTop: '1px solid #E0E4E8',
          padding: 'clamp(24px, 4vw, 44px) clamp(14px, 4vw, 40px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 20,
          justifyContent: 'space-between',
          fontSize: 13,
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <strong>{config.name}</strong>
          <span style={{ opacity: 0.6 }}>{config.bio}</span>
          <span style={{ opacity: 0.6 }}>{config.hours}</span>
        </div>
        <span style={{ opacity: 0.45 }}>{config.domain}</span>
      </footer>
    </div>
  );
}
